import { createSlice } from "@reduxjs/toolkit";

export const billingManagementSlice = createSlice({
  name: "billingManagement",
  initialState: {
    // GET Customers List
    isFetchingCustomers: false,
    customers: [],
    fetchCustomersError: null,

    // GET Billing Rules (Global)
    isFetchingRules: false,
    rules: [],
    fetchRulesError: null,

    // GET Customer Rules
    isFetchingCustomerRules: false,
    customerRules: [],
    fetchCustomerRulesError: null,

    // GET Customer Metadata
    isFetchingMetadata: false,
    metadata: null,
    fetchMetadataError: null,

    // POST Create Rules (Batch)
    isCreatingRules: false,
    createRulesSuccess: null,
    createRulesError: null,

    // PUT Update Rule
    isUpdatingRule: false,
    updateRuleSuccess: null,
    updateRuleError: null,

    // PUT Update Rules (Batch)
    isUpdatingRules: false,
    updateRulesSuccess: null,
    updateRulesError: null,

    // GET Billing Profile
    isFetchingProfile: false,
    billingProfile: null,
    fetchProfileError: null,

    // PUT Update Billing Profile
    isUpdatingProfile: false,
    updateProfileSuccess: null,
    updateProfileError: null,

    // Customer Rules metadata
    nextInvoiceDate: null,

    // Pagination
    pagination: {
      page: 1,
      page_size: 25,
      total: 0,
    },

    // GET MSP Billing Metrics (overview page)
    mspMetrics: null,
    isFetchingMetrics: false,
    metricsError: null,

    // GET Customer Invoices
    isFetchingInvoices: false,
    invoices: [],
    fetchInvoicesError: null,
    invoicesPagination: { page: 1, page_size: 25, total: 0 },
    hasInvoiceForMonth: null,

    // GET Invoice Rule Snapshots (audit)
    isFetchingInvoiceRuleSnapshots: false,
    invoiceRuleSnapshots: null,
    fetchInvoiceRuleSnapshotsError: null,

    // GET Customer Billing Metrics (customer invoices page, scope: customers)
    customerMetrics: null,
    isFetchingCustomerMetrics: false,
    customerMetricsError: null,
  },
  reducers: {
    // 🔄 GET Customers List
    getCustomers: (state) => {
      state.isFetchingCustomers = true;
      state.fetchCustomersError = null;
    },
    getCustomersSuccess: (state, action) => {
      state.isFetchingCustomers = false;
      // Handle different response structures
      if (action.payload) {
        // If payload has items array, use it
        if (Array.isArray(action.payload.items)) {
          state.customers = action.payload.items;
        } 
        // If payload is already an array, use it directly
        else if (Array.isArray(action.payload)) {
          state.customers = action.payload;
        }
        // If payload has items property but it's not an array, default to empty
        else if (action.payload.items !== undefined) {
          state.customers = Array.isArray(action.payload.items) ? action.payload.items : [];
        }
        // Default to empty array
        else {
          state.customers = [];
        }
        
        // Update pagination if available
        if (action.payload.page !== undefined) {
          state.pagination = {
            page: action.payload.page,
            page_size: action.payload.page_size || 25,
            total: action.payload.total || 0,
          };
        }
      } else {
        state.customers = [];
      }
    },
    getCustomersFailure: (state, action) => {
      state.isFetchingCustomers = false;
      state.fetchCustomersError = action.payload;
    },

    // 🔄 GET Billing Rules (Global)
    getBillingRules: (state) => {
      state.isFetchingRules = true;
      state.fetchRulesError = null;
    },
    getBillingRulesSuccess: (state, action) => {
      state.isFetchingRules = false;
      state.rules = action.payload.items || action.payload || [];
      if (action.payload.page !== undefined) {
        state.pagination = {
          page: action.payload.page,
          page_size: action.payload.page_size,
          total: action.payload.total || 0,
        };
      }
    },
    getBillingRulesFailure: (state, action) => {
      state.isFetchingRules = false;
      state.fetchRulesError = action.payload;
    },

    // 🔄 GET Customer Rules
    getCustomerRules: (state) => {
      state.isFetchingCustomerRules = true;
      state.fetchCustomerRulesError = null;
    },
    getCustomerRulesSuccess: (state, action) => {
      state.isFetchingCustomerRules = false;
      state.customerRules = action.payload.items || action.payload || [];
      if (action.payload.page !== undefined) {
        state.pagination = {
          page: action.payload.page,
          page_size: action.payload.page_size,
          total: action.payload.total || 0,
        };
      }
      // Capture next invoice date if provided
      state.nextInvoiceDate = action.payload.next_invoice_date || null;
    },
    getCustomerRulesFailure: (state, action) => {
      state.isFetchingCustomerRules = false;
      state.fetchCustomerRulesError = action.payload;
    },

    // 🔄 GET Customer Metadata
    getCustomerMetadata: (state) => {
      state.isFetchingMetadata = true;
      state.fetchMetadataError = null;
    },
    getCustomerMetadataSuccess: (state, action) => {
      state.isFetchingMetadata = false;
      state.metadata = action.payload;
    },
    getCustomerMetadataFailure: (state, action) => {
      state.isFetchingMetadata = false;
      state.fetchMetadataError = action.payload;
    },

    // 📄 POST Create Rules (Batch)
    createRules: (state) => {
      state.isCreatingRules = true;
      state.createRulesError = null;
      state.createRulesSuccess = null;
    },
    createRulesSuccess: (state, action) => {
      state.isCreatingRules = false;
      state.createRulesSuccess = action.payload;
    },
    createRulesFailure: (state, action) => {
      state.isCreatingRules = false;
      state.createRulesError = action.payload;
    },

    // 📝 PUT Update Rule
    updateRule: (state) => {
      state.isUpdatingRule = true;
      state.updateRuleError = null;
      state.updateRuleSuccess = null;
    },
    updateRuleSuccess: (state, action) => {
      state.isUpdatingRule = false;
      state.updateRuleSuccess = action.payload;
    },
    updateRuleFailure: (state, action) => {
      state.isUpdatingRule = false;
      state.updateRuleError = action.payload;
    },

    // 📝 PUT Update Rules (Batch)
    updateRules: (state) => {
      state.isUpdatingRules = true;
      state.updateRulesError = null;
      state.updateRulesSuccess = null;
    },
    updateRulesSuccess: (state, action) => {
      state.isUpdatingRules = false;
      state.updateRulesSuccess = action.payload;
    },
    updateRulesFailure: (state, action) => {
      state.isUpdatingRules = false;
      state.updateRulesError = action.payload;
    },

    // 🔄 GET Billing Profile
    getBillingProfile: (state) => {
      state.isFetchingProfile = true;
      state.fetchProfileError = null;
    },
    getBillingProfileSuccess: (state, action) => {
      state.isFetchingProfile = false;
      state.billingProfile = action.payload;
    },
    getBillingProfileFailure: (state, action) => {
      state.isFetchingProfile = false;
      state.fetchProfileError = action.payload;
    },

    // 📝 PUT Update Billing Profile
    updateBillingProfile: (state) => {
      state.isUpdatingProfile = true;
      state.updateProfileError = null;
      state.updateProfileSuccess = null;
    },
    updateBillingProfileSuccess: (state, action) => {
      state.isUpdatingProfile = false;
      state.updateProfileSuccess = action.payload;
      state.billingProfile = action.payload;
    },
    updateBillingProfileFailure: (state, action) => {
      state.isUpdatingProfile = false;
      state.updateProfileError = action.payload;
    },

    // GET Customer Invoices
    getCustomerInvoices: (state) => {
      state.isFetchingInvoices = true;
      state.fetchInvoicesError = null;
    },
    getCustomerInvoicesSuccess: (state, action) => {
      state.isFetchingInvoices = false;
      state.invoices = action.payload.items ?? [];
      state.invoicesPagination = {
        page: action.payload.page ?? 1,
        page_size: action.payload.page_size ?? 25,
        total: action.payload.total ?? 0,
      };
      state.hasInvoiceForMonth = action.payload.has_invoice_for_month ?? null;
    },
    getCustomerInvoicesFailure: (state, action) => {
      state.isFetchingInvoices = false;
      state.fetchInvoicesError = action.payload;
    },

    // GET MSP Billing Metrics
    getMspBillingMetrics: (state) => {
      state.isFetchingMetrics = true;
      state.metricsError = null;
    },
    getMspBillingMetricsSuccess: (state, action) => {
      state.isFetchingMetrics = false;
      state.mspMetrics = action.payload;
    },
    getMspBillingMetricsFailure: (state, action) => {
      state.isFetchingMetrics = false;
      state.metricsError = action.payload;
    },

    // GET Billing Metrics (Customer scope)
    getCustomerBillingMetrics: (state) => {
      state.isFetchingCustomerMetrics = true;
      state.customerMetricsError = null;
      state.customerMetrics = null;
    },
    getCustomerBillingMetricsSuccess: (state, action) => {
      state.isFetchingCustomerMetrics = false;
      state.customerMetrics = action.payload;
    },
    getCustomerBillingMetricsFailure: (state, action) => {
      state.isFetchingCustomerMetrics = false;
      state.customerMetricsError = action.payload;
    },

    // GET Invoice Rule Snapshots (audit)
    getInvoiceRuleSnapshots: (state) => {
      state.isFetchingInvoiceRuleSnapshots = true;
      state.fetchInvoiceRuleSnapshotsError = null;
      state.invoiceRuleSnapshots = null;
    },
    getInvoiceRuleSnapshotsSuccess: (state, action) => {
      state.isFetchingInvoiceRuleSnapshots = false;
      state.invoiceRuleSnapshots = action.payload;
    },
    getInvoiceRuleSnapshotsFailure: (state, action) => {
      state.isFetchingInvoiceRuleSnapshots = false;
      state.fetchInvoiceRuleSnapshotsError = action.payload;
    },

    // ♻️ RESET
    clearBillingManagementMessages: (state) => {
      state.createRulesSuccess = null;
      state.createRulesError = null;
      state.updateRuleSuccess = null;
      state.updateRuleError = null;
      state.updateRulesSuccess = null;
      state.updateRulesError = null;
      state.updateProfileSuccess = null;
      state.updateProfileError = null;
    },

    clearBillingManagementState: (state) => {
      state.customers = [];
      state.fetchCustomersError = null;
      state.rules = [];
      state.fetchRulesError = null;
      state.customerRules = [];
      state.fetchCustomerRulesError = null;
      state.nextInvoiceDate = null;
      state.metadata = null;
      state.fetchMetadataError = null;
      state.createRulesSuccess = null;
      state.createRulesError = null;
      state.updateRuleSuccess = null;
      state.updateRuleError = null;
      state.updateRulesSuccess = null;
      state.updateRulesError = null;
      state.pagination = {
        page: 1,
        page_size: 25,
        total: 0,
      };
    },
  },
});

export const {
  getCustomers,
  getCustomersSuccess,
  getCustomersFailure,
  getMspBillingMetrics,
  getMspBillingMetricsSuccess,
  getMspBillingMetricsFailure,
  getCustomerBillingMetrics,
  getCustomerBillingMetricsSuccess,
  getCustomerBillingMetricsFailure,
  getBillingRules,
  getBillingRulesSuccess,
  getBillingRulesFailure,
  getCustomerRules,
  getCustomerRulesSuccess,
  getCustomerRulesFailure,
  getCustomerMetadata,
  getCustomerMetadataSuccess,
  getCustomerMetadataFailure,
  createRules,
  createRulesSuccess,
  createRulesFailure,
  updateRule,
  updateRuleSuccess,
  updateRuleFailure,
  updateRules,
  updateRulesSuccess,
  updateRulesFailure,
  getBillingProfile,
  getBillingProfileSuccess,
  getBillingProfileFailure,
  updateBillingProfile,
  updateBillingProfileSuccess,
  updateBillingProfileFailure,
  getCustomerInvoices,
  getCustomerInvoicesSuccess,
  getCustomerInvoicesFailure,
  getInvoiceRuleSnapshots,
  getInvoiceRuleSnapshotsSuccess,
  getInvoiceRuleSnapshotsFailure,
  clearBillingManagementMessages,
  clearBillingManagementState,
} = billingManagementSlice.actions;

export default billingManagementSlice.reducer;
